import React, {
  FC,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
  RefObject,
} from 'react';
import clsx from 'clsx';
import Dismiss from './UI/Dismiss';
import useChatUIState from '../useChatUIState';
import { CHAT_FOOTER_HEIGHT } from './ChatContent';
import styles from './SlideableContent.module.css';

interface SlideableContentProps {
  footerEl: RefObject<HTMLDivElement | null>;
  scrollContainerEl: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}

// component logic for sliding the chat convo into and out of view,
// as well as mobile viewport/keyboard handling and touch events.
const SlideableContent: FC<SlideableContentProps> = ({
  footerEl,
  scrollContainerEl,
  children,
}) => {
  const [isPartialOpen, setIsPartialOpen] = useState(false);
  const [isFullOpening, setIsFullOpening] = useState(false);
  const [isFullOpen, setIsFullOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [mobileKeyboardVisible, setMobileKeyboardVisible] = useState(false);
  const [scrollStartY, setScrollStartY] = useState<number | null>(null);

  const chatContainerEl = useRef<HTMLDivElement | null>(null);
  const topBarEl = useRef<HTMLDivElement | null>(null);

  const { state: chatUIState, update: updateChatState } = useChatUIState();
  const { openOnLoad, chatOpen, hadChatInteraction, currentScrollTop } =
    chatUIState;

  const handleViewportResize = () => {
    if (window?.visualViewport?.offsetTop === 0)
      setMobileKeyboardVisible(false);
    else setMobileKeyboardVisible(true);
  };

  const preventDefaultScroll = (e: TouchEvent) => {
    e.preventDefault();
    e.stopImmediatePropagation();
  };

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (
        (scrollStartY &&
          e.touches[0].pageY < scrollStartY &&
          scrollContainerEl.current?.scrollTop === currentScrollTop) ||
        (scrollStartY &&
          e.touches[0].pageY > scrollStartY &&
          scrollContainerEl.current?.scrollTop === 0)
      ) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    [currentScrollTop, scrollContainerEl, scrollStartY],
  );

  const handleTouchStart = (e: TouchEvent) => {
    setScrollStartY(e.touches[0].pageY);
  };

  useEffect(() => {
    if (!mobileKeyboardVisible) return;

    const chatContainerRef = chatContainerEl.current;

    chatContainerRef &&
      chatContainerRef.addEventListener('touchmove', handleTouchMove);

    return () => {
      chatContainerRef &&
        chatContainerRef.removeEventListener('touchmove', handleTouchMove);
    };
  }, [mobileKeyboardVisible, scrollStartY, handleTouchMove]);

  useEffect(() => {
    if (!mobileKeyboardVisible) return;

    const topBarRef = topBarEl.current;
    topBarRef && topBarRef.addEventListener('touchmove', preventDefaultScroll);

    const footerRef = footerEl.current;
    footerRef && footerRef.addEventListener('touchmove', preventDefaultScroll);

    chatContainerEl.current?.addEventListener('touchstart', handleTouchStart);

    return () => {
      window?.visualViewport?.removeEventListener(
        'resize',
        handleViewportResize,
      );
      topBarRef &&
        topBarRef.removeEventListener('touchmove', preventDefaultScroll);
      footerRef &&
        footerRef.addEventListener('touchmove', preventDefaultScroll);
    };
  }, [mobileKeyboardVisible, footerEl]);

  useEffect(() => {
    if (typeof window !== 'object' || !window.visualViewport) return;
    window.visualViewport.addEventListener('resize', handleViewportResize);

    // when the chat is partly open on initial load or when fully
    // open, expand its height to take up the whole viewport
    if (
      chatContainerEl.current &&
      ((mobileKeyboardVisible && isPartialOpen) || isFullOpen)
    ) {
      chatContainerEl.current.style['height'] = `${
        window.visualViewport?.height - CHAT_FOOTER_HEIGHT
      }px`;
    }

    // on initial view before any user interaction, keep the chat
    // half height of the viewport if the keyboard is closed.
    if (chatContainerEl.current && isPartialOpen && !mobileKeyboardVisible) {
      chatContainerEl.current.style['height'] = 'auto';
    }

    return () => {
      window?.visualViewport?.removeEventListener(
        'resize',
        handleViewportResize,
      );
    };
  }, [isPartialOpen, isFullOpen, mobileKeyboardVisible]);

  useEffect(() => {
    // behavior of chat only on initial load
    if (openOnLoad && !hadChatInteraction) {
      setIsPartialOpen(true);
    }

    // behavior when chat opens after initial load or on user interaction
    if (hadChatInteraction && chatOpen) {
      setIsFullOpening(true);
    }
  }, [openOnLoad, chatOpen, hadChatInteraction]);

  // don't let the background page scroll when chat is open
  useEffect(() => {
    if (typeof window !== 'object') return;

    if (chatOpen) {
      document.body.style.position = 'fixed';
      document.body.style.top = `-${window.scrollY}px`;
    }

    if (!chatOpen) {
      document.body.style.position = '';
      document.body.style.top = '';
    }
  }, [chatOpen]);

  useEffect(() => {
    let fullOpeningTimer: ReturnType<typeof setTimeout>;

    if (isFullOpening) {
      const _timeout = 500;

      fullOpeningTimer = setTimeout(() => {
        setIsFullOpening(false);
        setIsPartialOpen(false);
        setIsFullOpen(true);
      }, _timeout);
    }

    return () => {
      if (fullOpeningTimer) {
        clearTimeout(fullOpeningTimer);
      }
    };
  }, [isFullOpening]);

  useEffect(() => {
    let closingTimer: ReturnType<typeof setTimeout>;

    if (isClosing) {
      const _timeout = 500;

      closingTimer = setTimeout(() => {
        setIsClosing(false);
        setIsPartialOpen(false);
        setIsFullOpen(false);
        updateChatState({
          ...chatUIState,
          chatOpen: false,
        });
      }, _timeout);
    }

    return () => {
      if (closingTimer) {
        clearTimeout(closingTimer);
      }
    };
  }, [isClosing, chatUIState, updateChatState]);

  const dismissOnClick = () => {
    setIsClosing(true);
  };

  let animationName = '';
  if (isFullOpening) animationName = styles.animateUp;
  else if (isClosing) animationName = styles.animateDown;

  const classes = clsx({
    [styles.container]: true,
    [animationName]: !!animationName,
    [styles.chatOpen]: chatOpen,
  });

  return (
    <>
      <div className={classes} ref={chatContainerEl}>
        <div className={styles.topChatBar} ref={topBarEl}>
          <div className={styles.dismissContainer}>
            <Dismiss
              onClick={() => {
                dismissOnClick();
              }}
            />
          </div>
        </div>
        {children}
      </div>
    </>
  );
};

export default SlideableContent;
